import type { NoteSummary } from './schema';
import { NOTE_STATUSES, NOTE_STATUS_LABELS, type NoteStatus } from './status';

/**
 * The three owner-facing intents the redesigned Notas screen groups the nine statuses into
 * (mockup principle: "the counts become actionable phrases", not a wall of status chips).
 */
export const NOTE_INTENTS = ['por_hacer', 'en_camino', 'en_excel'] as const;
export type NoteIntent = (typeof NOTE_INTENTS)[number];

export const NOTE_INTENT_LABELS: Readonly<Record<NoteIntent, string>> = {
  por_hacer: 'Por hacer',
  en_camino: 'En camino',
  en_excel: 'En Excel',
};

const INTENT_BY_STATUS: Readonly<Record<NoteStatus, NoteIntent>> = {
  awaiting_review: 'por_hacer',
  blocked_reference: 'por_hacer',
  delivery_attention: 'por_hacer',
  failed: 'por_hacer',
  submitted: 'en_camino',
  processing: 'en_camino',
  ready_for_excel: 'en_camino',
  delivering: 'en_camino',
  exported: 'en_excel',
};

export function noteIntent(status: NoteStatus): NoteIntent {
  return INTENT_BY_STATUS[status];
}

/** How many notes in `rows` fall under each intent - the counts the segment control shows. */
export function countByIntent(
  rows: readonly { readonly status: NoteStatus }[],
): Record<NoteIntent, number> {
  const counts: Record<NoteIntent, number> = { por_hacer: 0, en_camino: 0, en_excel: 0 };
  for (const row of rows) {
    counts[noteIntent(row.status)] += 1;
  }
  return counts;
}

/** Every status under one intent, in the fixed `NOTE_STATUSES` order - what the "Filtrar" sheet
 * lists under each of its two headers ("POR HACER" is checkable; "EN CAMINO · EN EXCEL" is the
 * collapsed explainer). */
export function statusesForIntent(intent: NoteIntent): readonly NoteStatus[] {
  return NOTE_STATUSES.filter((status) => noteIntent(status) === intent);
}

export interface NoteAction {
  readonly label: string;
  /** `'link'` navigates; `'retry'` calls the retry-recognition action already wired on the row;
   * `'restore'` brings a discarded note back where it stood. */
  readonly kind: 'link' | 'retry' | 'restore';
  readonly to?: string;
}

export interface NoteRowPresentation {
  /** Small colored dot tone - never the only cue, `message` always carries the meaning too. */
  readonly tone: 'neutral' | 'info' | 'warning' | 'danger' | 'success';
  readonly message: string;
  readonly helper?: string;
  readonly primaryAction?: NoteAction;
  readonly secondaryAction?: NoteAction;
}

const TONE_BY_STATUS: Readonly<Record<NoteStatus, NoteRowPresentation['tone']>> = {
  submitted: 'neutral',
  processing: 'info',
  awaiting_review: 'warning',
  blocked_reference: 'danger',
  ready_for_excel: 'success',
  delivering: 'info',
  exported: 'success',
  delivery_attention: 'danger',
  failed: 'danger',
};

function blockedMessage(customerResolved: boolean, openIssues: number): string {
  if (!customerResolved && openIssues >= 2) {
    return `Bloqueada: falta el cliente y ${String(openIssues - 1)} concepto${openIssues - 1 === 1 ? '' : 's'}`;
  }
  if (!customerResolved) {
    return 'Bloqueada: falta el cliente';
  }
  if (openIssues === 1) {
    return 'Bloqueada: falta 1 concepto';
  }
  return `Bloqueada: faltan ${String(openIssues)} conceptos`;
}

const PROVIDER_UNAVAILABLE_MESSAGE = 'El servicio está saturado. Intenta más tarde.';

const SHORT_FAILURE_MESSAGE: Readonly<Record<string, string>> = {
  image_unreadable: 'No se pudo leer: poca luz o borrosa',
  provider_unavailable: PROVIDER_UNAVAILABLE_MESSAGE,
  provider_rejected: 'No se pudo leer: la foto fue rechazada',
  invalid_model_output: 'No se pudo leer: resultado inválido',
  internal_error: 'No se pudo leer: error interno',
};

/**
 * One-line, actionable Spanish phrase plus the (at most two) buttons a note row shows - the
 * status-badge cluster the old design used, replaced with what CLAUDE.md calls for: "falta el
 * cliente" instead of "2 referencias". Every field this reads already exists on `NoteSummary`;
 * `blockedMessage` approximates which reference is missing from the counts that summary carries
 * (`customer_is_resolved`, `open_reference_issues`) rather than inventing a per-field breakdown
 * the backend does not (yet) return - see the redesign's reported schema gap.
 */
export function noteRowPresentation(note: NoteSummary): NoteRowPresentation {
  const tone = TONE_BY_STATUS[note.status];
  // A discarded row says so before it says anything else. Its `status` is still what the note
  // was when it was thrown away, which the helper line repeats so the owner can tell a note
  // they discarded mid-review from one they discarded before it was ever read.
  if (note.discarded_at !== null) {
    return {
      tone: 'neutral',
      message: note.discarded_reason ?? 'Descartada',
      helper: `Estaba en "${NOTE_STATUS_LABELS[note.status]}". Nada se borró.`,
      primaryAction: { label: 'Recuperar', kind: 'restore' },
      secondaryAction: { label: 'Abrir', kind: 'link', to: `/notas/${note.id}` },
    };
  }
  switch (note.status) {
    case 'awaiting_review':
      return {
        tone,
        message: 'Lista para que la revises',
        primaryAction: { label: 'Revisar', kind: 'link', to: `/notas/${note.id}` },
      };
    case 'blocked_reference':
      return {
        tone,
        message: blockedMessage(note.customer_is_resolved, Math.max(note.open_reference_issues, 1)),
        primaryAction: { label: 'Resolver', kind: 'link', to: `/notas/${note.id}?resolver=1` },
        secondaryAction: { label: 'Abrir', kind: 'link', to: `/notas/${note.id}` },
      };
    case 'failed':
      if (note.failure_code === 'provider_unavailable') {
        return {
          tone,
          message: PROVIDER_UNAVAILABLE_MESSAGE,
          helper: 'La nota se guardó. Puedes volver a intentar la lectura más tarde.',
          primaryAction: { label: 'Reintentar lectura', kind: 'retry' },
          secondaryAction: { label: 'Abrir', kind: 'link', to: `/notas/${note.id}` },
        };
      }
      return {
        tone,
        message: SHORT_FAILURE_MESSAGE[note.failure_code ?? ''] ?? 'No se pudo leer',
        helper: 'Vuelve a tomarla; esta se queda como referencia.',
        primaryAction: { label: 'Volver a tomar', kind: 'link', to: '/capturar' },
        secondaryAction: { label: 'Reintentar lectura', kind: 'retry' },
      };
    case 'delivery_attention':
      return {
        tone,
        message: 'Comprueba Excel: la entrega no se confirmó',
        primaryAction: { label: 'Revisar', kind: 'link', to: `/notas/${note.id}` },
      };
    case 'submitted':
      return {
        tone,
        message: 'En cola para lectura. La nota ya se guardó.',
        helper: 'Su estado se actualizará aquí.',
      };
    case 'processing':
      return {
        tone,
        message: 'Leyendo la nota.',
        helper: 'Su estado se actualizará aquí.',
      };
    case 'ready_for_excel':
      return { tone, message: 'Lista para Excel; se enviará pronto' };
    case 'delivering':
      return { tone, message: 'Enviando a Excel…' };
    case 'exported':
      return { tone, message: 'Enviada a Excel' };
    default:
      return { tone, message: NOTE_STATUS_LABELS[note.status] };
  }
}
